//frontend/src/components/FinanceReport/FinanceReport.jsx

import React, { useEffect, useMemo, useState } from "react";
import Button from "../UI/Button";
import Modal from "../UI/Modal";
import Dropdown from "../UI/Dropdown";
import Card from "../UI/Card";
import ConfirmModal from "../UI/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api/api";
import "./FinanceReport.css";
import { useTranslation } from "react-i18next";
import FinanceOverview from "./FinanceOverview";



const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/finances/accounts/`;

const TRON_URL = 'https://api.binance.com/api/v3/ticker/price?symbol=TRXUSDT'; //апи от binance

const emptyForm = {
  name: "",
  currency: "USD",
  comment: "",
};

const currencies = ["USD", "RUB", "CNY", "USDT", "TRX"];

/* символы валют */
const currencySymbols = {
  USD: "$",
  RUB: "₽",
  CNY: "¥",
  USDT: "₮",
  TRX: "TRX",
};

export default function FinanceReport() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [rates, setRates] = useState(null);
  const [marketRates, setMarketRates] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState("CNY");
  const [tronPrice, setTronPrice] = useState(null);
  const [flash, setFlash] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [view, setView] = useState("accounts"); // accounts | report

  const { t } = useTranslation("FinanceReport");
  const navigate = useNavigate();

  const trxPerUsd = tronPrice ? 1 / Number(tronPrice) : null;





  /* ================= ACCOUNTS ================= */
  const fetchAccounts = async () => {
    const res = await authFetch(API_URL);
    const data = await res.json();
    console.log("ACCOUNTS API RESPONSE:", data);
    setAccounts(Array.isArray(data) ? data : data.results || data.data || []);
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  /* ================= RATES ================= */
useEffect(() => {
  const fetchRates = async () => {
      const res = await fetch("https://open.er-api.com/v6/latest/USD");
      const data = await res.json();
      setRates(data.rates);
  };
    fetchRates();
  }, []);



  /*======= CRYPTO_RATES =======*/
useEffect(() => {
  const fetchTron = async () => {
    try {
      const res = await fetch(TRON_URL);
      const data = await res.json();
      setTronPrice(Number(data.price));
    } catch (err) {
      console.error(err);
    }
  };
  fetchTron();
}, []);


useEffect(() => {
  if (rates || tronPrice) {
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 400);
    return () => clearTimeout(t);
  }
}, [rates, tronPrice]);




  

  /* ================= CRUD ================= */
  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setIsOpen(true);
  };
  

  const openEdit = (acc) => {
    setForm({
      name: acc.name,
      currency: acc.currency,
      comment: acc.comment || "",
    });
    setEditingId(acc.id);
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const url = editingId
      ? `${API_URL}${editingId}/`
      : API_URL;

    await authFetch(url, {
      method: editingId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });

    setIsOpen(false);
    setForm(emptyForm);
    setEditingId(null);
    fetchAccounts();
  };

  const handleDelete = async () => {
    if (!accountToDelete) return;

    await authFetch(`${API_URL}${accountToDelete.id}/`, {
      method: "DELETE",
    });

    setAccounts((prev) =>
      prev.filter((a) => a.id !== accountToDelete.id)
    );

    setShowDeleteConfirm(false);
    setAccountToDelete(null);
  };



  const toUSD = (amount, from) => {
    if (from === "USD" || from === "USDT") return amount;
    if (from === "TRX") {
      if (!tronPrice) return null;
      return amount * Number(tronPrice);
    }
    if (!rates || !rates[from]) return null;
    return amount / rates[from];
  };

  const totalUSD = accounts.reduce((sum, acc) => {
    return sum + toUSD(Number(acc.balance), acc.currency);
  }, 0);


  const fromUSD = (amountUSD, to) => {
    if (to === "USD" || to === "USDT") return amountUSD;
    if (to === "TRX") {
      if (!tronPrice) return null;
      return amountUSD / Number(tronPrice);
    }
    if (!rates || !rates[to]) return null;
    return amountUSD * rates[to];
  };
  
  const totalInSelectedCurrency = useMemo(() => {
    let totalUSD = 0;
    for (const acc of accounts) {
      const v = toUSD(Number(acc.balance), acc.currency);
      if (v === null) return null;
      totalUSD += v;
    }
    return fromUSD(totalUSD, displayCurrency);
  }, [accounts, rates, tronPrice, displayCurrency]);


  const convert = (amount, from, to) => {
    const usd = toUSD(amount, from);
    return fromUSD(usd, to);
  };

  return (
    <div className="w-full max-w-4xl mx-auto mt-10">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            onClick={() => setView("accounts")}
            variant={view === "accounts" ? "primary" : "secondary"}
          >
            Счета
          </Button>
          <Button
            onClick={() => setView("report")}
            variant={view === "report" ? "primary" : "secondary"}
          >
            Финансовая отчетность
          </Button>
        </div>
        <h2 className="text-2xl font-bold">{t("title")}</h2>
        <Button onClick={openCreate} variant="primary">
          {t("create_account")}
        </Button>
      </div>


  {view === "accounts" && (
    <>
      {/* ================= TOTAL ================= */}
      <div className="p-4 border rounded-xl mb-6 bg-gray-50">
        <p className="text-sm text-gray-500">{t("total_balance")} </p>

        <p className="text-2xl font-bold text-black">
          {totalInSelectedCurrency === null
            ? "—"
            : `${currencySymbols[displayCurrency] || ""} ${totalInSelectedCurrency.toFixed(2)}`}
        </p>
        <select
          value={displayCurrency}
          onChange={(e) => setDisplayCurrency(e.target.value)}
          className="border p-2 rounded"
        >
          {currencies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
  


      <div className="ticker-wrapper">
        <div className="ticker-track">
          <div className="ticker-item">USD {rates?.USD?.toFixed(2)}</div>
          <div className="ticker-item">USD/CNY {rates?.CNY?.toFixed(2)}</div>
          <div className="ticker-item">USD/RUB {rates?.RUB?.toFixed(2)}</div>
          <div className="ticker-item">TRX/USDT {tronPrice?.toFixed(3)}</div>
          <div className="ticker-item">USDT/TRX {trxPerUsd?.toFixed(3)}</div>
          {/* ДУБЛЬ для бесконечного эффекта */}
          <div className="ticker-item">USD {rates?.USD?.toFixed(2)}</div>
          <div className="ticker-item">USD/CNY {rates?.CNY?.toFixed(2)}</div>
          <div className="ticker-item">USD/RUB {rates?.RUB?.toFixed(2)}</div>
          <div className="ticker-item">TRX/USDT {tronPrice?.toFixed(3)}</div>
          <div className="ticker-item">USDT/TRX {trxPerUsd?.toFixed(3)}</div>
        </div>
      </div>

      {/* ================= ACCOUNTS ================= */}
      <div className="space-y-4">
        {accounts.map((acc) => (
          <Card
            key={acc.id}
            hoverable
            className="flex justify-between items-start"
            onClick={() => {
              console.log("CLICK");
              navigate(`/finance/accounts/${acc.id}`);
            }}
          >
            
            <div>
              <p className="font-semibold text-lg">{acc.name}</p>
              <p className="text-lg font-medium">
                {currencySymbols[acc.currency] || acc.currency}{" "}
                {acc.balance}
              </p>

              <p className="text-gray-500">{acc.comment || "-"}</p>



              {/*конвертация валюты каждого аккаунта*/}
            <p className="text-sm text-gray-400">
              {(() => {
                const v = convert(Number(acc.balance), acc.currency, displayCurrency);
                return v === null ? "≈ —" : `≈ ${v.toFixed(2)} ${displayCurrency}`;
              })()}
            </p>

            </div>

            <div className="flex gap-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(acc);
                }}
                variant="edit"
              >
                {t("edit")}
              </Button>

              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setAccountToDelete(acc);
                  setShowDeleteConfirm(true);
                }}
                variant="danger"
              >
                {t("delete")}
              </Button>
            </div>
          </Card>
        ))}
      </div>
  </>
  )}

{view === "report" && <FinanceOverview />}


      {/* ================= MODAL ================= */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? t("edit_account") : t("create_account_modal")}
      >
        <form onSubmit={handleSubmit} className="space-y-4">

          <input
            className="w-full border p-2 rounded-xl"
            placeholder={t("name")}
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            required
          />

          <Dropdown
            value={form.currency}
            options={currencies.map((c) => ({
              id: c,
              label: c,
            }))}
            getLabel={(o) => o.label}
            onChange={(v) =>
              setForm({ ...form, currency: v })
            }
          />

          <input
            className="w-full border p-2 rounded-xl"
            placeholder={t("comment")}
            value={form.comment}
            onChange={(e) =>
              setForm({ ...form, comment: e.target.value })
            }
          />

          <button className="w-full bg-black text-white py-2 rounded-full">
            {editingId ? t("save") : t("create")}
          </button>
        </form>
      </Modal>

      {/* ================= DELETE ================= */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t("delete_account")}
        description={`${t("delete_confirm")} ${accountToDelete?.name}?`}
        confirmText="Удалить"
      />
    </div>
  );
}